/**
 * Утилита для форматирования ошибок API в понятные сообщения для пользователя
 */

interface ValidationError {
    [field: string]: string[];
}

/**
 * Маппинг типичных ошибок Identity на русский язык
 */
const identityErrorTranslations: { [key: string]: string } = {
    // Password errors
    'Passwords must have at least one non alphanumeric character.': 'Пароль должен содержать хотя бы один специальный символ (!@#$%^&* и т.д.)',
    'Passwords must have at least one digit (\'0\'-\'9\').': 'Пароль должен содержать хотя бы одну цифру (0-9)',
    'Passwords must have at least one uppercase (\'A\'-\'Z\').': 'Пароль должен содержать хотя бы одну заглавную букву (A-Z)',
    'Passwords must have at least one lowercase (\'a\'-\'z\').': 'Пароль должен содержать хотя бы одну строчную букву (a-z)',
    'Passwords must be at least 6 characters.': 'Пароль должен содержать не менее 6 символов',

    // User errors
    'User name is already taken.': 'Пользователь с таким именем уже существует',
    'Email is already taken.': 'Пользователь с таким email уже существует',
    'Invalid email.': 'Неверный формат email адреса',
    'User not found.': 'Пользователь не найден',

    // General
    'Invalid token.': 'Неверный токен',
    'Lockout is not enabled for this user.': 'Блокировка не включена для этого пользователя',
};

interface ApiErrorResponse {
    Message?: string;
    message?: string;
    errors?: ValidationError;
    error?: string;
    title?: string;
    detail?: string;
}

/**
 * Форматирует ошибку API в читаемое сообщение
 * @param error - Объект ошибки или Response
 * @param defaultMessage - Сообщение по умолчанию
 * @returns Отформатированное сообщение об ошибке
 */
export const formatApiError = async (
    error: any,
    defaultMessage: string = 'Произошла ошибка'
): Promise<string> => {
    try {
        // Если это Response объект
        if (error instanceof Response) {
            const contentType = error.headers.get('content-type');

            // Пробуем распарсить JSON
            if (contentType?.includes('application/json')) {
                try {
                    const errorData: ApiErrorResponse = await error.json();
                    return formatErrorData(errorData, defaultMessage);
                } catch {
                    // Если не удалось распарсить JSON, пробуем текст
                    const text = await error.text();
                    return text || `${defaultMessage} (HTTP ${error.status})`;
                }
            } else {
                // Если не JSON, получаем текст
                const text = await error.text();
                return text || `${defaultMessage} (HTTP ${error.status})`;
            }
        }

        // Если это уже объект ошибки
        if (typeof error === 'object' && error !== null) {
            return formatErrorData(error, defaultMessage);
        }

        // Если это строка
        if (typeof error === 'string') {
            return error || defaultMessage;
        }

        // Если это Error объект
        if (error instanceof Error) {
            return error.message || defaultMessage;
        }

        return defaultMessage;
    } catch {
        return defaultMessage;
    }
};

/**
 * Проверяет, является ли объект словарем ошибок валидации
 * ModelState от ASP.NET возвращает объект вида: { "": ["error1"], "FieldName": ["error2"] }
 */
const isValidationErrorObject = (obj: any): boolean => {
    if (typeof obj !== 'object' || obj === null) return false;

    // Проверяем, что все значения - массивы строк
    for (const value of Object.values(obj)) {
        if (!Array.isArray(value)) return false;
        if (value.length > 0 && typeof value[0] !== 'string') return false;
    }

    return Object.keys(obj).length > 0;
};

/**
 * Форматирует объект данных об ошибке
 */
const formatErrorData = (errorData: ApiErrorResponse, defaultMessage: string): string => {
    // Проверяем наличие сообщения
    if (errorData.Message) {
        return formatWithValidationErrors(errorData.Message, errorData.errors);
    }

    if (errorData.message) {
        return formatWithValidationErrors(errorData.message, errorData.errors);
    }

    if (errorData.detail) {
        return errorData.detail;
    }

    if (errorData.title) {
        return errorData.title;
    }

    if (errorData.error) {
        return errorData.error;
    }

    // Если есть только ошибки валидации
    if (errorData.errors) {
        return formatValidationErrors(errorData.errors);
    }

    // Проверяем, не является ли сам объект словарем ошибок ModelState
    // ASP.NET Identity возвращает: {"": ["error1", "error2"]}
    if (isValidationErrorObject(errorData)) {
        return formatValidationErrors(errorData as ValidationError);
    }

    return defaultMessage;
};

/**
 * Форматирует сообщение с добавлением ошибок валидации
 */
const formatWithValidationErrors = (
    message: string,
    errors?: ValidationError
): string => {
    if (!errors || Object.keys(errors).length === 0) {
        return message;
    }

    const validationDetails = formatValidationErrors(errors);
    return `${message}\n\n${validationDetails}`;
};

/**
 * Переводит сообщение об ошибке на русский, если есть перевод
 */
const translateErrorMessage = (message: string): string => {
    // Сначала проверяем точное соответствие
    if (identityErrorTranslations[message]) {
        return identityErrorTranslations[message];
    }

    // Затем проверяем паттерны для динамических сообщений
    if (message.includes('already taken') || message.includes('уже занято') || message.includes('уже занят')) {
        // "User name 'email' is already taken." или "Имя пользователя 'email' уже занято."
        if (message.includes('User name') || message.includes('Имя пользователя')) {
            return 'Пользователь с таким email уже существует';
        }
        return 'Это значение уже используется';
    }

    return message;
};

/**
 * Форматирует ошибки валидации в читаемый вид
 */
const formatValidationErrors = (errors: ValidationError): string => {
    const errorMessages: string[] = [];

    for (const [field, messages] of Object.entries(errors)) {
        if (Array.isArray(messages)) {
            messages.forEach(msg => {
                const translatedMsg = translateErrorMessage(msg);
                // Если поле пустое (Identity использует пустую строку для общих ошибок)
                if (!field || field.trim() === '') {
                    errorMessages.push(`• ${translatedMsg}`);
                } else {
                    errorMessages.push(`• ${field}: ${translatedMsg}`);
                }
            });
        } else {
            const translatedMsg = translateErrorMessage(messages);
            if (!field || field.trim() === '') {
                errorMessages.push(`• ${translatedMsg}`);
            } else {
                errorMessages.push(`• ${field}: ${translatedMsg}`);
            }
        }
    }

    return errorMessages.length > 0
        ? `Ошибки:\n${errorMessages.join('\n')}`
        : 'Неизвестные ошибки валидации';
};

/**
 * Упрощенная версия для синхронных ошибок
 */
export const formatError = (error: any, defaultMessage: string = 'Произошла ошибка'): string => {
    if (error instanceof Error) {
        return error.message || defaultMessage;
    }

    if (typeof error === 'string') {
        return error || defaultMessage;
    }

    if (typeof error === 'object' && error !== null) {
        if (error.Message) return error.Message;
        if (error.message) return error.message;
        if (error.detail) return error.detail;
        if (error.title) return error.title;
        if (error.error) return error.error;
    }

    return defaultMessage;
};
